<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-message-text-outline</v-icon>
        Palautteet
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Hae palautteista"
          single-line
          hide-details
          style="max-width: 300px"
        ></v-text-field>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="selectedType"
              :items="typeOptions"
              label="Tyyppi"
              clearable
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="selectedState"
              :items="stateOptions"
              label="Tila"
              clearable
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="selectedReceipt"
              :items="receiptOptions"
              label="Kuittaus"
              clearable
              outlined
              dense
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="reporterSearch"
              label="Hae ilmoittajaa"
              prepend-icon="mdi-account-search"
              outlined
              dense
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateRangeText"
                  label="Päivämäärä"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateRange"
                range
                @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-btn
              color="primary"
              @click="loadFeedback"
              :loading="loading"
              outlined
            >
              <v-icon left>mdi-refresh</v-icon>
              Päivitä
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="selected.length > 0" class="mt-3">
          <v-col cols="12">
            <v-card outlined>
              <v-card-text class="py-2">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <v-icon color="primary" class="mr-2">mdi-checkbox-multiple-marked</v-icon>
                    {{ selected.length }} palautetta valittu
                  </div>
                  <v-btn
                    color="success"
                    @click="showNotificationDialog = true"
                    :disabled="!canSendReceipt"
                    outlined
                  >
                    <v-icon left>mdi-email-send</v-icon>
                    Lähetä kuittaus
                  </v-btn>
                  <div v-if="receiptErrorMessage" class="text-caption text-error mt-1">
                    {{ receiptErrorMessage }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="filteredFeedback"
          :loading="loading"
          :search="search"
          :sort-by="['time']"
          :sort-desc="[true]"
          class="elevation-1"
          :items-per-page="20"
          show-select
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100]
          }"
        >
          <template v-slot:item.type="{ item }">
            <v-chip
              :color="getTypeColor(item.type)"
              small
              text-color="white"
            >
              {{ item.type }}
            </v-chip>
          </template>

          <template v-slot:item.receipt_sent="{ item }">
            <v-chip
              :color="item.receipt_sent ? 'info' : 'grey'"
              small
              text-color="white"
            >
              {{ item.receipt_sent ? 'Lähetetty' : 'Ei lähetetty' }}
            </v-chip>
          </template>

          <template v-slot:item.time="{ item }">
            {{ formatDate(item.time) }}
          </template>

          <template v-slot:item.feedback="{ item }">
            <div class="text-truncate" style="max-width: 300px;">
              {{ item.feedback }}
            </div>
          </template>

          <template v-slot:item.state="{ item }">
            <v-menu
              v-model="item.stateMenu"
              :close-on-content-click="true"
              offset-y
              min-width="140px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  v-bind="attrs"
                  v-on="on"
                  :color="getStatusChip(item).color"
                  text-color="white"
                  style="cursor: pointer; min-width: 90px; font-size: 12px; padding: 0 14px; height: 32px; letter-spacing: 0.2px;"
                >
                  <v-progress-circular
                    v-if="item.updating"
                    indeterminate
                    size="16"
                    width="2"
                    color="white"
                    class="mr-1"
                  />
                  <span>{{ getStatusChip(item).text }}</span>
                  <v-icon right small>mdi-menu-down</v-icon>
                </v-chip>
              </template>
              <v-list>
                <v-list-item
                  v-for="option in [
                    { text: 'Käsittelemättä', value: 'received', color: 'warning' },
                    { text: 'Korjattu', value: 'fixed', color: 'success' },
                    { text: 'Hylätty', value: 'denied', color: 'error' }
                  ]"
                  :key="option.value"
                  @click="() => selectState(item, option.value)"
                  :disabled="item.updating || item.state === option.value"
                >
                  <v-chip
                    :color="option.color"
                    text-color="white"
                    style="font-size: 14px; font-weight: 600; padding: 0 12px; height: 28px; min-width: 80px; letter-spacing: 0.2px;"
                  >
                    {{ option.text }}
                  </v-chip>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              small
              @click="viewFeedback(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              v-if="item.event_id"
              @click="openEvent(item)"
            >
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Feedback Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-message-text</v-icon>
          Palautteen tiedot
          <v-spacer></v-spacer>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="selectedFeedback">
          <v-row>
            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-title class="text-subtitle-1">Perustiedot</v-card-title>
                <v-card-text>
                  <div class="mb-2">
                    <strong>Tyyppi:</strong>
                    <v-chip
                      :color="getTypeColor(selectedFeedback.type)"
                      small
                      class="ml-2"
                    >
                      {{ selectedFeedback.type }}
                    </v-chip>
                  </div>
                  <div class="mb-2" v-if="selectedFeedback">
                    <strong>Päivämäärä:</strong> {{ formatDate(selectedFeedback.time) }}
                  </div>
                  <div class="mb-2" v-if="selectedFeedback">
                    <strong>Ilmoittaja:</strong> {{ selectedFeedback.reporter }}
                  </div>
                  <div class="mb-2" v-if="selectedFeedback">
                    <strong>Tila:</strong>
                    <v-chip
                      :color="selectedFeedback.state === 'fixed' ? 'success' : selectedFeedback.state === 'denied' ? 'error' : 'warning'"
                      small
                      class="ml-2"
                    >
                      {{ selectedFeedback.state === 'fixed' ? 'Korjattu' : selectedFeedback.state === 'denied' ? 'Hylätty' : 'Käsittelemättä' }}
                    </v-chip>
                  </div>
                  <div class="mb-2" v-if="selectedFeedback && selectedFeedback.fixed_time">
                    <strong>Korjattu:</strong> {{ formatDate(selectedFeedback.fixed_time) }}
                  </div>
                  <div class="mb-2" v-if="selectedFeedback && selectedFeedback.receipt_sent">
                    <strong>Ilmoitus lähetetty:</strong> {{ formatDate(selectedFeedback.receipt_sent) }}
                  </div>
                  <div class="mb-2" v-if="selectedFeedback && selectedFeedback.game_id">
                    <strong>Peli ID:</strong> {{ selectedFeedback.game_id }}
                  </div>
                  <div class="mb-2" v-if="selectedFeedback && selectedFeedback.event_id">
                    <strong>Tapahtuma ID:</strong> {{ selectedFeedback.event_id }}
                  </div>
                  <div class="mb-2" v-if="selectedFeedback && selectedFeedback.run_id">
                    <strong>Juoksu ID:</strong> {{ selectedFeedback.run_id }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined>
                <v-card-title class="text-subtitle-1">Palautekommentti</v-card-title>
                <v-card-text>
                  <div class="text-body-1" v-if="selectedFeedback">
                    {{ selectedFeedback.feedback || 'Ei kommenttia' }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="showDeleteConfirmation = true"
            :loading="deleting"
            v-if="selectedFeedback && selectedFeedback.state !== 'denied'"
          >
            <v-icon left>mdi-delete</v-icon>
            Poista
          </v-btn>
          <v-btn
            :color="selectedFeedback && selectedFeedback.state === 'fixed' ? 'warning' : 'success'"
            outlined
            @click="toggleFixedStatus"
            :loading="updating"
            v-if="selectedFeedback && selectedFeedback.state !== 'denied'"
          >
            <v-icon left>{{ selectedFeedback && selectedFeedback.state === 'fixed' ? 'mdi-undo' : 'mdi-check' }}</v-icon>
            {{ selectedFeedback && selectedFeedback.state === 'fixed' ? 'Merkkaa käsittelemättömäksi' : 'Merkkaa korjatuksi' }}
          </v-btn>
          <v-btn
            color="error"
            outlined
            @click="denyFeedback"
            :loading="updating"
            v-if="selectedFeedback && selectedFeedback.state !== 'denied'"
          >
            <v-icon left>mdi-cancel</v-icon>
            Hylkää
          </v-btn>
          <v-btn
            color="info"
            outlined
            @click="restoreFeedback"
            :loading="updating"
            v-if="selectedFeedback && selectedFeedback.state === 'denied'"
          >
            <v-icon left>mdi-restore</v-icon>
            Palauta käsittelemättömäksi
          </v-btn>
          <v-btn
            color="primary"
            @click="detailDialog = false"
          >
            Sulje
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <ConfirmationDialog
      :show="showDeleteConfirmation"
      text="Poista palautte"
      subText="Haluatko varmasti poistaa tämän palautteen? Tätä toimintoa ei voi perua."
      btnColor="error"
      btnText="Poista"
      @accept="confirmDelete"
      @decline="showDeleteConfirmation = false"
    />

    <!-- Notification Dialog -->
    <v-dialog v-model="showNotificationDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-email-send</v-icon>
          Lähetä kuittaus käyttäjille
          <v-spacer></v-spacer>
          <v-btn icon @click="showNotificationDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <div class="text-subtitle-2 font-weight-medium mb-2">Valitut palautteet:</div>
            <div class="text-body-2">
              <div v-for="feedback in selected" :key="feedback.id" class="mb-1">
                • {{ feedback.type }} - {{ formatDate(feedback.time) }}
                <span v-if="feedback.reporter" class="text-caption">({{ feedback.reporter }})</span>
                <v-chip v-if="feedback.fixed_time" color="success" x-small class="ml-1">Korjattu</v-chip>
                <v-chip v-else color="warning" x-small class="ml-1">Käsittelemättä</v-chip>
                <v-chip v-if="feedback.receipt_sent" color="info" x-small class="ml-1">Kuittaus lähetetty</v-chip>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <div>
            <div class="text-subtitle-2 font-weight-medium mb-2">Lisätietoja kuittaukseen:</div>
            <v-textarea
              v-model="notificationMessage"
              outlined
              rows="4"
              placeholder="Lisätietoja korjauksesta... (valinnainen)"
              hint="Tämä tieto lisätään kuittausviestiin käyttäjille. Viesti on valinnainen."
              persistent-hint
            ></v-textarea>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="showNotificationDialog = false"
          >
            Peruuta
          </v-btn>
          <v-btn
            color="success"
            @click="sendNotifications"
            :loading="sendingNotifications"
          >
            <v-icon left>mdi-send</v-icon>
            Lähetä
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import a from '@/utils/axios/axios'
import { mapActions } from 'vuex'
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'

export default {
  name: 'AdminFeedback',
  components: {
    ConfirmationDialog
  },
  data: () => ({
    feedbackRaw: [],
    loading: false,
    search: '',
    reporterSearch: '',
    selectedType: null,
    selectedState: null,
    selectedReceipt: null,
    dateRange: [],
    dateMenu: false,
    detailDialog: false,
    selectedFeedback: null,
    deleting: false,
    updating: false,
    showDeleteConfirmation: false,
    selected: [],
    showNotificationDialog: false,
    notificationMessage: '',
    sendingNotifications: false,
    headers: [
      { text: 'Tyyppi', value: 'type', width: '120px' },
      { text: 'Tila', value: 'state', width: '100px' },
      { text: 'Päivämäärä', value: 'time', width: '150px' },
      { text: 'Ilmoittaja', value: 'reporter', width: '200px' },
      { text: 'Ilmoitus lähetetty', value: 'receipt_sent', width: '150px' },
      { text: 'Peli ID', value: 'game_id', width: '100px' },
      { text: 'Tapahtuma ID', value: 'event_id', width: '120px' },
      { text: 'Juoksu ID', value: 'run_id', width: '100px' },
      { text: 'Kommentti', value: 'feedback', width: '300px' },
      { text: 'Toiminnot', value: 'actions', sortable: false, width: '80px' }
    ],
    typeOptions: [
      { text: 'event', value: 'event' },
      { text: 'run', value: 'run' },
      { text: 'play', value: 'play' },
    ],
    statusOptions: [
      { text: 'Käsittelemättä', value: 'pending' },
      { text: 'Korjattu', value: 'fixed' },
      { text: 'Hylätty', value: 'denied' },
      { text: 'Kuittaus lähetetty', value: 'receipt_sent' },
      { text: 'Kuittaus lähettämättä', value: 'no_receipt' }
    ],
    stateOptions: [
      { text: 'Käsittelemättä', value: 'received' },
      { text: 'Korjattu', value: 'fixed' },
      { text: 'Hylätty', value: 'denied' }
    ],
    receiptOptions: [
      { text: 'Kaikki', value: null },
      { text: 'Lähetetty', value: 'sent' },
      { text: 'Ei lähetetty', value: 'not_sent' }
    ]
  }),
  computed: {
    feedback() {
      // Always return items with stateMenu initialized
      return this.feedbackRaw.map(item => ({
        ...item,
        stateMenu: item.stateMenu !== undefined ? item.stateMenu : false
      }))
    },
    filteredFeedback() {

      let filtered = this.feedback

      if (this.selectedType) {
        filtered = filtered.filter(item => item.type === this.selectedType)
      }

      if (this.selectedState) {
        filtered = filtered.filter(item => item.state === this.selectedState)
      }

      if (this.selectedReceipt) {
        if (this.selectedReceipt === 'sent') {
          filtered = filtered.filter(item => item.receipt_sent)
        } else if (this.selectedReceipt === 'not_sent') {
          filtered = filtered.filter(item => !item.receipt_sent)
        }
      }

      if (this.reporterSearch) {
        filtered = filtered.filter(item =>
          item.reporter && item.reporter.toLowerCase().includes(this.reporterSearch.toLowerCase())
        )
      }

      if (this.dateRange && this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0])
        const endDate = new Date(this.dateRange[1])
        filtered = filtered.filter(item => {
          const itemDate = new Date(item.time)
          return itemDate >= startDate && itemDate <= endDate
        })
      }

      return filtered
    },
    dateRangeText() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        return 'Valitse päivämäärä'
      }
      return `${this.dateRange[0]} - ${this.dateRange[1]}`
    },
    selectedFeedbackForReceipt() {
      return this.selected.filter(feedback => !feedback.receipt_sent)
    },
    hasSelectedFeedbackForReceipt() {
      return this.selectedFeedbackForReceipt.length > 0
    },
    canSendReceipt() {
      if (this.selected.length === 0) return false

      // Check if any selected feedback already has receipt sent
      const hasReceiptSent = this.selected.some(feedback => feedback.receipt_sent)
      if (hasReceiptSent) return false

      // Check if all feedback is from the same user
      const reporters = this.selected.map(feedback => feedback.reporter).filter(Boolean)
      if (reporters.length === 0) return false

      const firstReporter = reporters[0]
      const allSameReporter = reporters.every(reporter => reporter === firstReporter)

      return allSameReporter
    },
    receiptErrorMessage() {
      if (this.selected.length === 0) return ''

      // Check if any selected feedback already has receipt sent
      const hasReceiptSent = this.selected.some(feedback => feedback.receipt_sent)
      if (hasReceiptSent) {
        return 'Jotkut valitut palautteet ovat jo saaneet kuittauksen'
      }

      // Check if all feedback is from the same user
      const reporters = this.selected.map(feedback => feedback.reporter).filter(Boolean)
      if (reporters.length === 0) {
        return 'Valitut palautteet eivät sisällä ilmoittajaa'
      }

      const firstReporter = reporters[0]
      const allSameReporter = reporters.every(reporter => reporter === firstReporter)
      if (!allSameReporter) {
        return 'Kaikkien palautteiden tulee olla samalta käyttäjältä'
      }

      return ''
    }
  },
  methods: {
    ...mapActions('noti', ['success', 'error']),
    getStatusChip(item) {
      if (item.state === 'fixed') {
        return { color: 'success', text: 'Korjattu' }
      } else if (item.state === 'denied') {
        return { color: 'error', text: 'Hylätty' }
      } else {
        return { color: 'warning', text: 'Käsittelemättä' }
      }
    },
    async loadFeedback() {
      this.loading = true
      try {
        const response = await a.get('/feedback')
        this.feedbackRaw = response.data
      } catch (err) {
        console.error('Error loading feedback:', err)
        this.error('Palautteiden lataus epäonnistui')
      } finally {
        this.loading = false
      }
    },
    openEvent(item) {
      const url = `${window.location.origin}/#/analysis/${item.game_id}?tab=${item.type === 'run' ? 4 : 1}&event_id=${item.event_id}`
      window.open(url, 'event_analysis', 'fullscreen=yes,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no')
    },
    getTypeColor(type) {
      const colors = {
        error: 'error',
        run: 'warning',
        play: 'info',
      }
      return colors[type] || 'grey'
    },
    getTypeText(type) {
      const texts = {
        error: 'Virhe',
        event: 'Tapahtuma',
        suggestion: 'Ehdotus',
        other: 'Muu'
      }
      return texts[type] || type
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('fi-FI', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    viewFeedback(feedback) {
      this.selectedFeedback = feedback
      this.detailDialog = true
    },
    confirmDelete() {
      this.showDeleteConfirmation = false
      this.deleteFeedback()
    },
    async deleteFeedback() {
      if (!this.selectedFeedback) return

      this.deleting = true
      try {
        await a.delete(`/feedback/${this.selectedFeedback.id}`)

        // Remove from local array
        const index = this.feedbackRaw.findIndex(item => item.id === this.selectedFeedback.id)
        if (index > -1) {
          this.feedbackRaw.splice(index, 1)
        }

        this.detailDialog = false
        this.selectedFeedback = null
        this.success('Palautte poistettu onnistuneesti')
      } catch (error) {
        console.error('Error deleting feedback:', error)
        this.error('Palautteen poisto epäonnistui')
      } finally {
        this.deleting = false
      }
    },
    async toggleFixedStatus() {
      if (!this.selectedFeedback || this.selectedFeedback.state === 'denied') return
      this.updating = true
      try {
        const fixed = this.selectedFeedback.state !== 'fixed'
        await a.patch(`/feedback/${this.selectedFeedback.id}/fixed`, { fixed })
        this.selectedFeedback.fixed_time = fixed ? new Date().toISOString() : null
        this.selectedFeedback.state = fixed ? 'fixed' : 'received'
        const index = this.feedbackRaw.findIndex(item => item.id === this.selectedFeedback.id)
        if (index > -1) {
          this.feedbackRaw[index].fixed_time = this.selectedFeedback.fixed_time
          this.feedbackRaw[index].state = this.selectedFeedback.state
        }
        this.success(fixed ? 'Palautte merkitty korjatuksi' : 'Palautte merkitty käsittelemättömäksi')
      } catch (error) {
        console.error('Error updating feedback status:', error)
        this.error('Palautteen tilan päivitys epäonnistui')
      } finally {
        this.updating = false
      }
    },
    async toggleFixedStatusForItem(item) {
      if (item.state === 'denied') return // Don't allow fixing denied
      this.$set(item, 'updating', true)
      try {
        const fixed = item.state !== 'fixed'
        await a.patch(`/feedback/${item.id}/fixed`, { fixed })
        item.fixed_time = fixed ? new Date().toISOString() : null
        item.state = fixed ? 'fixed' : 'received'
        const index = this.feedbackRaw.findIndex(feedback => feedback.id === item.id)
        if (index > -1) {
          this.feedbackRaw[index].fixed_time = item.fixed_time
          this.feedbackRaw[index].state = item.state
        }
        this.success(fixed ? 'Palautte merkitty korjatuksi' : 'Palautte merkitty käsittelemättömäksi')
      } catch (error) {
        console.error('Error updating feedback status:', error)
        this.error('Palautteen tilan päivitys epäonnistui')
      } finally {
        this.$set(item, 'updating', false)
      }
    },
    async denyFeedback() {
      if (!this.selectedFeedback) return
      this.updating = true
      try {
        await a.patch(`/feedback/${this.selectedFeedback.id}/deny`)
        this.selectedFeedback.state = 'denied'
        this.selectedFeedback.fixed_time = null
        const index = this.feedbackRaw.findIndex(item => item.id === this.selectedFeedback.id)
        if (index > -1) {
          this.feedbackRaw[index].state = 'denied'
          this.feedbackRaw[index].fixed_time = null
        }
        this.success('Palautte hylätty')
      } catch (error) {
        console.error('Error denying feedback:', error)
        this.error('Palautteen hylkäys epäonnistui')
      } finally {
        this.updating = false
      }
    },
    async restoreFeedback() {
      if (!this.selectedFeedback) return
      this.updating = true
      try {
        await a.patch(`/feedback/${this.selectedFeedback.id}/fixed`, { fixed: false })
        this.selectedFeedback.state = 'received'
        this.selectedFeedback.fixed_time = null
        const index = this.feedbackRaw.findIndex(item => item.id === this.selectedFeedback.id)
        if (index > -1) {
          this.feedbackRaw[index].state = 'received'
          this.feedbackRaw[index].fixed_time = null
        }
        this.success('Palautte palautettu käsittelemättömäksi')
      } catch (error) {
        console.error('Error restoring feedback:', error)
        this.error('Palautteen palautus epäonnistui')
      } finally {
        this.updating = false
      }
    },
        async sendNotifications() {
      if (!this.canSendReceipt) return

      this.sendingNotifications = true
      try {
        const feedbackIds = this.selected.map(feedback => feedback.id)

        await a.patch('/feedback/receipt-sent', {
          ids: feedbackIds,
          extraInfo: this.notificationMessage
        })

        // Update local feedback data
        const now = new Date().toISOString()
        feedbackIds.forEach(id => {
          const feedback = this.feedbackRaw.find(f => f.id === id)
          if (feedback) {
            feedback.receipt_sent = now
          }
        })

        // Clear selection and close dialog
        this.selected = []
        this.showNotificationDialog = false
        this.notificationMessage = ''

        this.success(`Kuittaus lähetetty ${feedbackIds.length} palautteesta`)
      } catch (error) {
        console.error('Error sending receipts:', error)
        this.error('Kuittauksen lähetys epäonnistui')
      } finally {
        this.sendingNotifications = false
      }
    },
    selectState(item, newState) {
      if (item.state === newState) return
      item.stateMenu = false
      item.state = newState
      this.onStateChange(item)
    },
    async onStateChange(item) {
      this.$set(item, 'updating', true)
      try {
        if (item.state === 'fixed') {
          await a.patch(`/feedback/${item.id}/fixed`, { fixed: true })
          item.fixed_time = new Date().toISOString()
        } else if (item.state === 'received') {
          await a.patch(`/feedback/${item.id}/fixed`, { fixed: false })
          item.fixed_time = null
        } else if (item.state === 'denied') {
          await a.patch(`/feedback/${item.id}/deny`)
          item.fixed_time = null
        }
        this.success('Tila päivitetty')
      } catch (error) {
        this.error('Tilan päivitys epäonnistui')
        this.loadFeedback()
      } finally {
        this.$set(item, 'updating', false)
      }
    }
  },
  mounted() {
    this.loadFeedback()
  }
}
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}
</style>